import { inject, Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";

import { MrdmAuthApiService } from "./mrdm-auth-api.service";
import { APPLICATION_INSTANCE, AuthorizationExtendedPermission, AuthorizationOrganization, PRODUCT } from "./mrdm-auth-api.types";

@Injectable({
    providedIn: "root"
})
export class MrdmAuthService {
    private _authorizationApiService = inject(MrdmAuthApiService);
    private _applicationInstance = inject(APPLICATION_INSTANCE);
    private _product = inject(PRODUCT);

    readonly user$ = this._authorizationApiService.getProfile().pipe(shareReplay(1));
    readonly userPermissions$ = this._searchPermissions$();
    readonly userOrganizations$ = this._getFilteredUserOrganizations$();

    readonly isInternalUser$ = this.user$.pipe(map(profile => profile.isInternal));

    getProductPermissions(projectProduct: string = ""): string[] {
        let permissions: string[] = [];
        this.userPermissions$
            .pipe(
                map(
                    permissions => permissions.find(perm => perm.product == projectProduct)?.permissions ?? []
                )
            )
            .subscribe(perms => (permissions = [...perms]));
        return permissions;
    }

    private _searchPermissions$(): Observable<AuthorizationExtendedPermission[]> {
        return this._authorizationApiService
            .searchPermissions({ applicationInstances: [ this._applicationInstance ] })
            .pipe(
                map(permissions =>  {
                    const dpPermissions = permissions.filter(perm =>
                        perm.product === this._product && (perm.permissions.includes("dpl_dataentry_view") || perm.permissions.includes("dpl_dataentry_edit"))
                    );
                    return permissions.filter(perm =>
                        dpPermissions.some(dpPerm => dpPerm.organizationId === perm.organizationId)
                    );
                }),
                shareReplay(1));
    }

    private _getFilteredOrganizationIdCodes$(): Observable<number[]> {
        return this.userPermissions$.pipe(
            map(permissions => {
                const filteredOrganizationIdCodes: number[] = permissions.map(item => item.organizationId)
                    .filter(organizationId => organizationId !== null) as number[];

                return [...new Set(filteredOrganizationIdCodes)];
            })
        );
    }

    private _getFilteredUserOrganizations$(): Observable<AuthorizationOrganization[]> {
        return combineLatest([this.user$, this._getFilteredOrganizationIdCodes$()]).pipe(
            map(([user, filteredOrganizationIdCodes]) => {
                const organizations = user.organizations;
                return organizations.filter(
                    organization =>
                        organization.organizationId !== null &&
                        filteredOrganizationIdCodes.includes(organization.organizationId)
                );
            })
        );
    }
}
